/**
 * @fileoverview Error Boundary for Authentication Components
 *
 * Provides error handling and recovery for authentication-related components.
 * Captures JavaScript errors anywhere in the auth component tree and displays
 * a fallback UI instead of crashing the entire application.
 *
 * @author Claude Code
 * @since Phase 4 - Code Quality Improvements
 */

import React from 'react';
import { Button } from '@/components/ui/button';

import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { useLocales } from '@/locales';
import { Home, RefreshCw } from 'lucide-react';

/**
 * @typedef {Object} ErrorInfo
 * @property {string} componentStack - Component stack trace
 */

/**
 * Error Boundary component for authentication flows
 *
 * @class AuthErrorBoundary
 * @extends {React.Component}
 */
class AuthErrorBoundary extends React.Component {
  /**
   * @param {Object} props - Component props
   * @param {React.ReactNode} props.children - Child components to wrap
   * @param {Function} [props.onError] - Optional error callback
   * @param {React.ComponentType} [props.fallback] - Custom fallback component
   */
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      errorId: null,
    };
  }

  /**
   * Static method to update state when an error occurs
   *
   * @param {Error} error - The error that occurred
   * @returns {Object} New state object
   */
  static getDerivedStateFromError(error) {
    return {
      hasError: true,
      error,
      errorId: Date.now().toString(36),
    };
  }

  /**
   * Called when an error occurs during rendering
   *
   * @param {Error} error - The error that occurred
   * @param {ErrorInfo} errorInfo - Additional error information
   */
  componentDidCatch(error, errorInfo) {
    this.setState({
      errorInfo,
    });

    // Log error for debugging
    console.error('Auth Error Boundary caught an error:', error, errorInfo);

    // Call optional error callback
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }

    // In production, you might want to log this to an error reporting service
    if (process.env.NODE_ENV === 'production') {
      // Log to error reporting service
      this.logErrorToService(error, errorInfo);
    }
  }

  /**
   * Logs error to external service (placeholder)
   *
   * @param {Error} error - The error to log
   * @param {ErrorInfo} errorInfo - Additional error information
   */
  logErrorToService = () => {
    // Placeholder for error reporting service integration
    // Example: Sentry, LogRocket, etc.
    console.warn('Error reporting service not configured');
  };

  /**
   * Handles retry action - resets error state
   */
  handleRetry = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
      errorId: null,
    });
  };

  /**
   * Handles navigation to home page
   */
  handleGoHome = () => {
    window.location.href = '/';
  };

  /**
   * Renders the component
   *
   * @returns {React.ReactElement} The rendered component
   */
  render() {
    if (this.state.hasError) {
      // Custom fallback component if provided
      if (this.props.fallback) {
        const FallbackComponent = this.props.fallback;
        return (
          <FallbackComponent
            error={this.state.error}
            errorInfo={this.state.errorInfo}
            onRetry={this.handleRetry}
            onGoHome={this.handleGoHome}
          />
        );
      }

      // Default error UI
      return (
        <DefaultErrorFallback
          {...this.state}
          onRetry={this.handleRetry}
          onGoHome={this.handleGoHome}
        />
      );
    }

    return this.props.children;
  }
}

/**
 * Default fallback component when an error occurs
 *
 * @param {Object} props - Component props
 * @param {Error} props.error - The error that occurred
 * @param {ErrorInfo} props.errorInfo - Additional error information
 * @param {string} props.errorId - Unique error identifier
 * @param {Function} props.onRetry - Retry handler
 * @param {Function} props.onGoHome - Navigate home handler
 * @returns {React.ReactElement} The fallback UI
 */
const DefaultErrorFallback = ({
  error,
  errorInfo,
  errorId,
  onRetry,
  onGoHome,
}) => {
  const { translate } = useLocales();

  return (
    <div
      className="flex flex-col items-center justify-center text-center"
      style={{
        minHeight: '400px',
        padding: 3,
      }}
    >
      <Alert variant="destructive" className="mb-6 max-w-[500px]">
        <AlertTitle>
          {translate('Authentication Error') || 'Authentication Error'}
        </AlertTitle>
        <AlertDescription>
          {translate(
            'Something went wrong during authentication. Please try again.',
          ) || 'Something went wrong during authentication. Please try again.'}
        </AlertDescription>
        {process.env.NODE_ENV === 'development' && (
          <div className="mt-2 font-mono text-xs break-words">
            Error ID: {errorId}
            <br />
            {error?.message}
          </div>
        )}
      </Alert>
      <div
        className="flex gap-4 justify-center"
        style={{
          flexWrap: 'wrap',
        }}
      >
        <Button variant="default" onClick={onRetry} className="min-w-[120px]">
          <RefreshCw className="mr-2 h-4 w-4" />
          {translate('Try Again') || 'Try Again'}
        </Button>
        <Button variant="outline" onClick={onGoHome} className="min-w-[120px]">
          <Home className="mr-2 h-4 w-4" />
          {translate('Go Home') || 'Go Home'}
        </Button>
      </div>
      {process.env.NODE_ENV === 'development' && errorInfo && (
        <div
          className="mt-6"
          style={{
            maxWidth: 600,
          }}
        >
          <span className="text-xs">
            Component Stack:
            <pre
              style={{ textAlign: 'left', overflow: 'auto', maxHeight: 200 }}
            >
              {errorInfo.componentStack}
            </pre>
          </span>
        </div>
      )}
    </div>
  );
};

/**
 * Hook for using error boundary functionality in functional components
 *
 * @returns {Function} Function to throw errors that will be caught by error boundary
 *
 * @example
 * const throwError = useErrorHandler();
 *
 * const handleSomeAction = () => {
 *   try {
 *     // Some risky operation
 *   } catch (error) {
 *     throwError(error);
 *   }
 * };
 */
export const useErrorHandler = () => {
  return React.useCallback(error => {
    // This will trigger the nearest error boundary
    throw error;
  }, []);
};

export default AuthErrorBoundary;
